import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Layout from "../../../../Components/layouts";
import { RouteSkeleton } from '../../../../Components/RouteTransition';
import { interactiveCardStyles, pageEyebrowStyles, pageTitleTextStyles } from '../../../../theme/commonStyles';
import { CategoryCount, categoryNavStyles } from '../categoryNavStyles';
import { SkillsAndTransformation } from 'Services/SkillsAndTransformation';
import { Button, CircularProgress, Grid, Input, TextField, Typography } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import MenuList from "@material-ui/core/MenuList";
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import MenuItem from "@material-ui/core/MenuItem";
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import { notify } from 'utils';
import { useImpersonation } from 'hooks/useImpersonation';
import { OPEN_COURSE_LINK_LABEL, OPEN_COURSE_LINK_NOTE } from 'utils/courseLaunch';
import ConfirmationModal from 'Components/ConfirmationModal';
import { UserServices } from '../../../../Services/UserServices';
import { useHistory, useLocation } from 'react-router';
import { DEFAULT_COURSE_IMAGE_URL } from 'utils/constants';
import { cardAccent, chipSurface } from '../LearningHub/helpers';
import {
    CATALOGUE_PAGE_SIZES,
    catalogueCategoriesFromResponse,
    catalogueCoursesFromResponse,
    cataloguePageIndex,
    cataloguePageSize,
    emptyCataloguePagination,
    resolveCataloguePagination,
} from './catalogueQuery';

const skillsAndTransformation = new SkillsAndTransformation();
const userServices = new UserServices();

const useStyles = makeStyles((theme) => ({
    pageHeader: {
        marginBottom: theme.spacing(2.5),
    },
    pageEyebrow: {
        ...pageEyebrowStyles,
        marginBottom: theme.spacing(0.5),
    },
    pageTitle: {
        ...pageTitleTextStyles,
        marginBottom: theme.spacing(0.5),
    },
    pageDescription: {
        color: '#647184',
        fontSize: '0.875rem',
        lineHeight: 1.55,
        maxWidth: 640,
    },
    layoutGrid: {
        alignItems: 'stretch',
    },
    ...categoryNavStyles(theme),
    cardDetails: {
        display: 'flex',
        flexDirection: 'column',
    },
    content: {
        flex: '1 0 auto',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflow: 'visible',
        boxSizing: 'border-box',
        padding: theme.spacing(2, 2.25),
        backgroundImage: 'linear-gradient(165deg, var(--card-wash, transparent) 0%, transparent 62%)',
        '&:last-child': {
            paddingBottom: theme.spacing(2),
        },
    },
    title: {
        fontWeight: 700,
        fontSize: '1rem',
        color: '#263548',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1.35,
        [theme.breakpoints.up('sm')]: {
            fontSize: '1.0625rem',
        },
    },
    desc: {
        margin: 0,
        color: '#647184',
        fontSize: '0.875rem',
        lineHeight: 1.45,
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    descBox: {
        flex: 1,
        minWidth: 0,
        marginTop: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    lockIcon: {
        fontSize: 20,
        color: '#94a3b8',
        flexShrink: 0,
    },
    unLockIcon: {
        fontSize: 20,
        color: '#12924a',
        flexShrink: 0,
    },
    mainContent: {
        minWidth: 0,
    },
    courseActionBtn: {
        borderRadius: '999px !important',
        minHeight: 36,
        padding: '0 16px',
        color: '#fff !important',
        fontSize: 13,
        fontWeight: 650,
        letterSpacing: '0.01em',
        minWidth: 0,
        textTransform: 'none',
        boxShadow: 'none !important',
        transition: 'filter 150ms ease',
        '&:hover': {
            boxShadow: 'none !important',
            filter: 'brightness(0.94)',
        },
    },
    requestBtn: {
        backgroundColor: 'var(--card-accent, #2f6fde) !important',
        '&:hover': {
            backgroundColor: 'var(--card-accent, #2f6fde) !important',
        },
    },
    launchBtn: {
        backgroundColor: '#12924a !important',
        '&:hover': {
            backgroundColor: '#12924a !important',
        },
    },
    cardRoot: {
        ...interactiveCardStyles,
        minHeight: 120,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflow: 'hidden',
        '& .MuiCardContent-root': {
            overflow: 'visible',
        },
        [theme.breakpoints.up('sm')]: {
            minHeight: 140,
        },
        [theme.breakpoints.up('md')]: {
            minHeight: 160,
        },
    },
    thumbnailWrap: {
        position: 'relative',
        width: '100%',
        backgroundColor: '#f5f8fc',
        borderBottom: '1px solid #e7ebf0',
        overflow: 'hidden',
        lineHeight: 0,
    },
    thumbnailPending: {
        minHeight: 180,
    },
    categoryOverlay: {
        position: 'absolute',
        top: 12,
        left: 12,
        zIndex: 1,
        maxWidth: 'calc(100% - 24px)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: '#fff',
        backgroundColor: 'rgba(23, 32, 51, 0.82)',
        borderRadius: 8,
        padding: '5px 10px',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.3,
    },
    thumbnail: {
        display: 'block',
        width: '100%',
        height: 'auto',
        opacity: 0,
        transition: 'opacity 0.2s ease',
    },
    thumbnailLoaded: {
        opacity: 1,
    },
    cardActionsRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        marginTop: 16,
        paddingTop: 12,
        gap: 8,
        minWidth: 0,
        flexShrink: 0,
        borderTop: '1px solid rgba(148, 163, 184, 0.28)',
    },
    inputStyle: {
        border: '1px solid #e7ebf0',
        borderRadius: 8,
        padding: theme.spacing(1.5),
        backgroundColor: '#fff',
        '&:hover, &:focus-within': {
            borderColor: '#d5dde8',
        },
    },
    coursesGrid: {
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1, 0),
        },
        alignItems: 'stretch',
    },
    emptyState: {
        color: '#647184',
        fontSize: '0.9375rem',
        fontWeight: 500,
        textAlign: 'center',
        padding: theme.spacing(4, 2),
        width: '100%',
    },
    toolbar: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    searchField: {
        flex: '1 1 220px',
        backgroundColor: '#fff',
    },
    pageSizeControl: {
        minWidth: 110,
        backgroundColor: '#fff',
    },
    resultLine: {
        color: '#647184',
        fontSize: '0.8125rem',
        fontWeight: 500,
    },
    gridLoading: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        padding: theme.spacing(6, 0),
    },
    paginationRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2),
    },
}));

const LazyThumbnail = ({ src, alt, wrapClassName, pendingClassName, imageClassName, loadedClassName, rootRef, overlay }) => {
    const placeholderRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        const node = placeholderRef.current;
        if (!node || isVisible) {
            return undefined;
        }

        if (typeof IntersectionObserver === 'undefined') {
            setIsVisible(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            {
                root: rootRef?.current || null,
                rootMargin: '240px 0px',
                threshold: 0.01,
            }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [isVisible, rootRef]);

    return (
        <div className={`${wrapClassName}${isLoaded ? '' : ` ${pendingClassName || ''}`}`} ref={placeholderRef}>
            {isVisible ? (
                <img
                    loading="lazy"
                    className={`${imageClassName}${isLoaded ? ` ${loadedClassName}` : ''}`}
                    src={src}
                    alt={alt}
                    decoding="async"
                    onLoad={() => setIsLoaded(true)}
                />
            ) : null}
            {overlay}
        </div>
    );
};

const catalogueCornerLabel = (course) => {
    const value = typeof course?.category === 'string' ? course.category.trim() : '';
    return value && value !== '0' ? value : '';
};

const CourseItem = ({ course, handleCourseAction, scrollRootRef }) => {
    const classes = useStyles();
    const impersonation = useImpersonation();
    const imageUrl = (course.imageUrl || course.thumbnail_url || '').trim() || DEFAULT_COURSE_IMAGE_URL;
    const cornerLabel = catalogueCornerLabel(course);
    const accent = cardAccent(cornerLabel || course.name);

    return (
        <Grid item xs={12} sm={6} md={6} lg={6}>
            <Card
                className={classes.cardRoot}
                style={{ '--card-accent': accent, '--card-wash': `${accent}29` }}
            >
                <LazyThumbnail
                    src={imageUrl}
                    alt={course.name}
                    wrapClassName={classes.thumbnailWrap}
                    pendingClassName={classes.thumbnailPending}
                    imageClassName={classes.thumbnail}
                    loadedClassName={classes.thumbnailLoaded}
                    rootRef={scrollRootRef}
                    overlay={cornerLabel ? (
                        <span className={classes.categoryOverlay} style={{ backgroundColor: chipSurface(accent) }}>
                            {cornerLabel}
                        </span>
                    ) : null}
                />
                <CardContent className={classes.content}>
                    <div className={classes.descBox}>
                        <Typography className={classes.title}>
                            {course.name}
                        </Typography>
                        <Typography variant="subtitle2" className={classes.desc}>
                            {course.description ? course.description : "Description not available"}
                        </Typography>
                    </div>
                    <div className={classes.cardActionsRow}>
                        {course.locked
                            ? <LockIcon className={classes.lockIcon} />
                            : <LockOpenIcon className={classes.unLockIcon} />}
                        <Button
                            className={`${classes.courseActionBtn} ${course.locked ? classes.requestBtn : classes.launchBtn}`}
                            onClick={() => handleCourseAction(course)}
                            variant="contained"
                            disableElevation
                        >
                            {course.locked ? "Request" : (impersonation.active ? OPEN_COURSE_LINK_LABEL : "Launch")}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </Grid >
    );
}

const catalogueDataCount = (response) => {
    const count = Number(response?.pagination?.data_count);
    return Number.isFinite(count) && count >= 0 ? count : null;
};

const CourseCatalogue = () => {

    const classes = useStyles();
    const history = useHistory();
    const impersonation = useImpersonation();
    const [loading, setLoading] = useState(true);
    const [listLoading, setListLoading] = useState(false);
    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [categories, setCategories] = useState([]);
    const [categoryCounts, setCategoryCounts] = useState({});
    const [allCoursesCount, setAllCoursesCount] = useState(null);
    const [courses, setCourses] = useState([]);
    const [pagination, setPagination] = useState(() => emptyCataloguePagination());
    const [page, setPage] = useState(0);
    const [perpage, setPerpage] = useState(12);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [requestLoading, setRequestLoading] = useState(false);
    const [user, setUser] = useState(null);
    const [comment, setComment] = useState("");
    const location = useLocation();
    const coursesPanelRef = useRef(null);
    const category = new URLSearchParams(location.search).get('category') || '';

    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim()), 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        setPage(0);
    }, [category, search, perpage]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, [category, page, search]);

    useEffect(() => {
        getUserDetails();
    }, [location.pathname]);

    useEffect(() => {
        let active = true;
        const loadCatalogue = async () => {
            setListLoading(true);
            const response = await skillsAndTransformation.getCourseCatalogue({
                pageno: cataloguePageIndex(page),
                perpage: cataloguePageSize(perpage),
                category,
                search,
            });
            if (!active) {
                return;
            }
            if (response?.status) {
                const nextCourses = catalogueCoursesFromResponse(response);
                const nextCategories = catalogueCategoriesFromResponse(response);
                setCourses(nextCourses);
                if (nextCategories.length) {
                    setCategories((current) => (
                        current.length === nextCategories.length
                        && current.every((name, index) => name === nextCategories[index])
                            ? current
                            : nextCategories
                    ));
                }
                const nextPagination = resolveCataloguePagination(response.pagination, {
                    pageno: page,
                    perpage,
                    courses: nextCourses,
                });
                setPagination(nextPagination);
                if (nextPagination.pageno !== page) {
                    setPage(nextPagination.pageno);
                }
            } else {
                setCourses([]);
                setPagination(emptyCataloguePagination(perpage));
                notify('error', response?.message || 'Unable to load the course catalogue');
            }
            setListLoading(false);
            setLoading(false);
        };
        loadCatalogue();
        return () => {
            active = false;
        };
    }, [category, page, perpage, search]);

    const categoryListKey = categories.join('\n');

    useEffect(() => {
        if (!categories.length) {
            return undefined;
        }
        let active = true;
        const loadCategoryCounts = async () => {
            const [totalResponse, ...categoryResponses] = await Promise.all([
                skillsAndTransformation.getCourseCatalogue({
                    pageno: 0,
                    perpage: 12,
                    search,
                }),
                ...categories.map((name) => skillsAndTransformation.getCourseCatalogue({
                    pageno: 0,
                    perpage: 12,
                    category: name,
                    search,
                })),
            ]);
            if (!active) {
                return;
            }
            const nextCounts = {};
            categories.forEach((name, index) => {
                const count = catalogueDataCount(categoryResponses[index]);
                if (count != null) {
                    nextCounts[name] = count;
                }
            });
            setCategoryCounts(nextCounts);
            const total = catalogueDataCount(totalResponse);
            if (total != null) {
                setAllCoursesCount(total);
            }
        };
        loadCategoryCounts();
        return () => {
            active = false;
        };
    }, [categoryListKey, search]);

    async function getUserDetails() {
        const user = await userServices.getDetailMyProfile();
        if (user && user.data && user.data.length) {
            setUser(user.data[0]);
        }
    }

    const handleCategory = (name) => {
        setPage(0);
        const params = new URLSearchParams(location.search);
        if (name) {
            params.set('category', name);
        } else {
            params.delete('category');
        }
        const searchText = params.toString();
        history.push({
            pathname: location.pathname,
            search: searchText ? `?${searchText}` : '',
        });
    };

    const handleSearchChange = (event) => {
        setSearchInput(event.target.value);
    };

    const handlePageSize = (event) => {
        setPerpage(cataloguePageSize(event.target.value));
        setPage(0);
    };

    const handleCourseAction = (course) => {
        if (course.locked) {
            setSelectedCourse(course);
            setOpenConfirmModal(true);
        } else if (course.link) {
            window.open(course.link, '_blank', 'noopener,noreferrer');
        } else {
            notify('error', "Requested link not available")
        }
    }

    const handleRequestSubmit = async () => {
        if (selectedCourse && user) {
            setRequestLoading(true);
            const payload = {
                course: selectedCourse.uuid,
                user: user.uuid,
                status: 'pending',
                comment
            };
            try {
                await skillsAndTransformation.sendCourseRequest(payload);
                setRequestLoading(false);
                setOpenConfirmModal(false);
                setSelectedCourse(null);
                setComment("");
                notify('success', "Request submited successfully");
            } catch (e) {
                setRequestLoading(false);
                setOpenConfirmModal(false);
                setSelectedCourse(null);
                console.log("Error: ", e.response);
                if (e.response && e.response.data) {
                    notify('error', e.response.data.message);
                    return null;
                }
                notify('error', "Something went wrong");
            }
        }
    }

    const visibleAllCount = !category && !listLoading
        ? pagination.data_count
        : (allCoursesCount != null ? allCoursesCount : (!category ? pagination.data_count : null));

    const showingCount = courses.length;
    const rangeStart = showingCount ? pagination.pageno * pagination.perpage + 1 : 0;
    const rangeEnd = showingCount ? rangeStart + showingCount - 1 : 0;

    return (
        <Layout>
            {loading ? (
                <RouteSkeleton variant="course-catalogue" />
            ) : (
                <>
            <div className={classes.pageHeader}>
                <Typography className={classes.pageEyebrow}>Skill Transformation</Typography>
                <Typography className={classes.pageTitle}>Course Catalogue</Typography>
                <Typography className={classes.pageDescription}>
                    {impersonation.active
                        ? OPEN_COURSE_LINK_NOTE
                        : 'Explore available courses by category and request or launch the ones you need.'}
                </Typography>
            </div>
            <Grid container spacing={2} className={classes.layoutGrid}>
                    <Grid item xs={12} md={3} className={classes.sidebarMenu}>
                        <Typography className={classes.sidebarTitle} component="h2">Course Category</Typography>
                        <MenuList id="simple-menu" open={true} className={classes.categoryList}>
                            <MenuItem
                                onClick={() => handleCategory('')}
                                className={`${classes.categoryItem}${!category ? ` ${classes.categoryItemActive}` : ''}`}
                            >
                                <span className={classes.categoryLabel}>All courses</span>
                                <CategoryCount
                                    count={visibleAllCount}
                                    active={!category}
                                    classes={classes}
                                />
                            </MenuItem>
                            {categories.map((name) => {
                                const isActive = name === category;
                                const count = isActive && !listLoading
                                    ? pagination.data_count
                                    : (categoryCounts[name] != null ? categoryCounts[name] : (isActive ? pagination.data_count : null));
                                return (
                                    <MenuItem
                                        key={name}
                                        onClick={() => handleCategory(name)}
                                        id={isActive ? 'selectedCategory' : ''}
                                        className={`${classes.categoryItem}${isActive ? ` ${classes.categoryItemActive}` : ''}`}
                                        title={name}
                                    >
                                        <span className={classes.categoryLabel}>{name}</span>
                                        <CategoryCount
                                            count={count}
                                            active={isActive}
                                            classes={classes}
                                        />
                                    </MenuItem>
                                );
                            })}
                        </MenuList>
                    </Grid>
                    <Grid item xs={12} md={9} className={classes.mainContent} ref={coursesPanelRef}>
                        <div className={classes.toolbar}>
                            <TextField
                                className={classes.searchField}
                                variant="outlined"
                                size="small"
                                placeholder="Search course name"
                                value={searchInput}
                                onChange={handleSearchChange}
                                inputProps={{ 'aria-label': 'Search course name' }}
                            />
                            <TextField
                                className={classes.pageSizeControl}
                                select
                                variant="outlined"
                                size="small"
                                label="Per page"
                                value={perpage}
                                onChange={handlePageSize}
                            >
                                {CATALOGUE_PAGE_SIZES.map((size) => (
                                    <MenuItem key={size} value={size}>{size}</MenuItem>
                                ))}
                            </TextField>
                            <Typography className={classes.resultLine}>
                                {pagination.data_count
                                    ? `${rangeStart}–${rangeEnd} of ${pagination.data_count}`
                                    : 'No courses'}
                            </Typography>
                        </div>
                        <Grid item container spacing={2} className={classes.coursesGrid}>
                            {listLoading ? (
                                <div className={classes.gridLoading}>
                                    <CircularProgress size={28} />
                                </div>
                            ) : courses.length > 0 ? courses.map(course => (
                                <CourseItem
                                    key={course.uuid || course.course_id || course.name}
                                    handleCourseAction={handleCourseAction}
                                    course={course}
                                    scrollRootRef={coursesPanelRef}
                                />
                            )) : (
                                <Typography className={classes.emptyState}>No courses in this category.</Typography>
                            )}
                        </Grid>
                        {!listLoading && pagination.total_pages > 1 ? (
                            <div className={classes.paginationRow}>
                                <Pagination
                                    page={pagination.pageno + 1}
                                    count={pagination.total_pages}
                                    onChange={(_event, value) => {
                                        if (value - 1 > pagination.pageno && !pagination.has_next) {
                                            return;
                                        }
                                        setPage(value - 1);
                                    }}
                                    color="primary"
                                    shape="rounded"
                                    variant="outlined"
                                />
                            </div>
                        ) : null}
                    </Grid>
            </Grid>
                </>
            )}
            <ConfirmationModal
                open={openConfirmModal}
                handleConfirm={handleRequestSubmit}
                title={<b>{`Request for ${selectedCourse && selectedCourse.name} Course`}</b>}
                loading={requestLoading}
                size={'sm'}
                handleClose={() => {
                    setSelectedCourse(null);
                    setOpenConfirmModal(false);
                }}>
                <Grid container style={{ marginTop: 10, marginBottom: 10 }}>
                    <Grid xs={12} item>
                        <Input
                            disableUnderline
                            value={comment}
                            type={'text'}
                            name="comment"
                            multiline
                            rows={4}
                            className={classes.inputStyle}
                            placeholder={"Comment"}
                            style={{ width: '100%' }}
                            color={'secondary'}
                            onChange={evt => setComment(evt.target.value)}
                        />
                    </Grid>
                </Grid>
            </ConfirmationModal>
        </Layout >
    );
}

export default CourseCatalogue;
